import { UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { LoaderSpinner } from "../LoaderSpinner";
import { SelectDropdown } from "../SelectDropdown";
import { toast } from "react-toastify";
import { validateEmail } from "../../helpers/management/vendors";

export const AddUserModal = ({
    isOpen,
    isLoading,
    userRolesOptions,
    cancelClick,
    submitClick,
}) => {
    const modalRef = useRef(null);
    const [fullName, setFullName] = useState("");
    const [slackId, setSlackId] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const handleClearInputs = () => {
        setFullName("");
        setSlackId("");
        setEmail("");
        setPassword("");
        setSelectedRole("");
    }
    useClickOutsideBlock(modalRef, cancelClick);

    useEffect(() => {
        if (!isOpen) {
            handleClearInputs();
        }
    }, [isOpen]);

    if (!isOpen) return null;
    return (
        <div aria-hidden="true" className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full">
            <div className="bg-gray-700 top-0 right-0 left-0 fixed z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full opacity-30" />
            <div ref={modalRef} className={`relative p-5 w-full max-w-lg max-h-full bg-white border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 rounded-md`}>
                <div className="flex justify-end">
                    <button 
                        onClick={cancelClick}
                        className="inline-flex items-center p-1 text-primary absolute"
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
                <div className="flex flex-col gap-4 text-center sm:text-left">
                    <h2 className="text-lg leading-none font-semibold flex items-center gap-2">
                        <UserIcon className="h-6 w-6 text-primary"/>
                        <span>Add New User</span>
                    </h2>
                    <p className="text-gray-700/90 text-sm">
                        Add a new user to the system and configure their initial permissions.
                    </p>
                </div>
                <div className="space-y-4 my-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="fullName">
                                Full Name
                            </label>
                            <input
                                id="fullName"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="emailAddress">
                                Email Address
                            </label>
                            <input
                                id="emailAddress"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="password">
                                Password
                            </label>
                            <input
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="role">
                                Role
                            </label>
                            <SelectDropdown
                                key={selectedRole}
                                options={userRolesOptions || []}
                                value={selectedRole}
                                onChange={setSelectedRole}
                                selectClassname="bg-white px-3 py-2 w-full"
                            />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm leading-none font-medium select-none" htmlFor="password">
                            Slack ID
                        </label>
                        <input
                            id="slackId"
                            value={slackId}
                            placeholder="Slack ID"
                            onChange={(e) => setSlackId(e.target.value)}
                            className="border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        />
                    </div>
                </div> 
                <div className="flex justify-end gap-2">
                    <button 
                        onClick={() => {
                            cancelClick()
                            handleClearInputs();
                        }}
                            className="min-w-20 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-3 py-1.5 bg-white text-xs font-medium text-gray-700 hover:text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                    >
                        {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Cancel"}
                    </button>
                    <button 
                        onClick={() => {
                            if (!isLoading) {
                                if (!selectedRole) {
                                    toast.error("Role is empty.", { position: "bottom-right" });
                                    return;
                                }
                                if (!password) {
                                    toast.error("Password is empty.", { position: "bottom-right" });
                                    return;
                                }
                                if (!validateEmail(email)) {
                                    toast.error("Invalid email.", { position: "bottom-right" });
                                    return;
                                }
                                submitClick({ fullName, email, password, selectedRole, slackId });
                            }
                        }}
                            className="min-w-24 justify-center inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                        {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Add User"}
                    </button>
                </div>
            </div>
        </div>
    )
}